/**
 * Which of an Image or Video node's references its prompt can name, and by what tag: @Image1…
 * for pictures, Kling's @Element1… for characters and objects, Seedance's @Video1… and
 * @Audio1… for clips and audio. Index by index over the node's references in the order they
 * are sent (utils/nodeInputs.ts), null where one is not named (not sent, or the mode names
 * nothing). Pure, so the nodes and the Claude connector tag references the same way.
 */
import type { ModelDefinition, VideoGenerationMode } from '../models/config';
import type { ReferenceTag } from '../types';
import type { AudioRole } from './audioReference';
import { klingSlots, type KlingSlot } from './klingReferences';
import { audioTag, imageTag, videoTag } from './promptMentions';
import { DEFAULT_REFERENCE_LIMITS } from './referenceClips';

/** How many of its pictures an Image node sends with this model, from the first: the prompt names them @Image1… */
export const imagePicturesSent = (model: ModelDefinition): number =>
    !model.supportsImageReference ? 0
        : model.provider === 'comfyui' ? 1
            : model.provider === 'openai' ? (model.maxReferenceImages || 16)
                : model.provider === 'venice' ? (model.editApiModelId ? (model.maxReferenceImages || 6) : 0)
                    : (model.maxReferenceImages || 1);

/** An Image node's tags: @Image1… for the pictures it sends, null for the rest */
export const imageReferenceTags = (model: ModelDefinition, pictureCount: number): (string | null)[] => {
    const sent = imagePicturesSent(model);
    return Array.from({ length: pictureCount }, (_, i) => (i < sent ? imageTag(i) : null));
};

/** The mode a Video node is in: the saved one when the model has it, else text to video */
export const videoModeOf = (model: ModelDefinition, saved?: string | null): VideoGenerationMode => {
    const modes = (model.supportedVideoModes as VideoGenerationMode[] | undefined) || ['text-to-video'];
    const mode = (saved || 'text-to-video') as VideoGenerationMode;
    return modes.includes(mode) ? mode : 'text-to-video';
};

/** The model takes reference clips in this mode (Seedance's reference mode) */
export const videoTakesClips = (model: ModelDefinition, mode: VideoGenerationMode): boolean =>
    model.provider === 'venice' && !!model.videoInputModes?.includes(mode);

/** The model takes audio in this mode (Seedance 2.x's reference mode) */
export const videoTakesAudio = (model: ModelDefinition, mode: VideoGenerationMode): boolean =>
    model.provider === 'venice' && !!model.audioInputModes?.includes(mode);

/** What a Video node's references may be chosen as, in this mode: pictures, and in reference mode also clips and audio where the model takes them */
export const videoPickKinds = (model: ModelDefinition, mode: VideoGenerationMode): ('image' | 'video' | 'audio')[] =>
    mode === 'text-to-video' || mode === 'extend-video' ? []
        : mode === 'reference-to-video'
            ? ['image', ...(videoTakesClips(model, mode) ? ['video' as const] : []), ...(videoTakesAudio(model, mode) ? ['audio' as const] : [])]
            : ['image'];

/**
 * How many of its pictures a Video node sends, from the first, outside Venice's reference mode
 * (which names and sends its own, see videoReferenceTags), as the node's generation code does:
 * Sora the first whenever there is one, whatever the mode; image to video the start frame;
 * frames to video the first and last frames; Veo's ingredients up to 3; text to video and
 * extend none. Clips and audio are sent only in reference mode.
 */
export const videoPicturesUsed = (model: ModelDefinition, mode: VideoGenerationMode): number =>
    model.provider === 'openai' ? 1
        : mode === 'image-to-video' ? 1
            : mode === 'frames-to-video' ? 2
                : mode === 'ingredients-to-video' ? 3
                    : mode === 'reference-to-video' ? (model.maxReferenceImages || 9)
                        : 0;

export interface VideoReferenceTags {
    images: (string | null)[];
    videos: (string | null)[];
    audio: (string | null)[];
    /** Kling's reference mode: each picture's role and tag (element or scene), over all of them */
    klingSlots: KlingSlot[] | null;
}

/**
 * A Video node's tags. Only Venice's reference mode names references in the prompt: the pictures
 * it sends (up to the model's number), for Kling by their role (@Element… and @Image…), else
 * @Image…; then for Seedance the clips (@Video…) and the audio (@Audio…: the one music file, or
 * as many voice and sound references as the model takes).
 */
export const videoReferenceTags = (
    model: ModelDefinition,
    mode: VideoGenerationMode,
    audioRole: AudioRole,
    images: ReadonlyArray<{ tag?: ReferenceTag }>,
    videoCount: number,
    audioCount: number,
): VideoReferenceTags => {
    const kling = model.referenceElements && mode === 'reference-to-video' ? klingSlots(images.map(image => image.tag)) : null;
    const none = (count: number) => Array.from({ length: count }, () => null as string | null);
    if (model.provider !== 'venice' || mode !== 'reference-to-video') {
        return { images: none(images.length), videos: none(videoCount), audio: none(audioCount), klingSlots: kling };
    }
    const maxPictures = model.maxReferenceImages || 9;
    const imageTags = images.map((_, i) => (i < maxPictures ? kling?.[i]?.tag ?? imageTag(i) : null));
    if (kling) return { images: imageTags, videos: none(videoCount), audio: none(audioCount), klingSlots: kling };
    const audioSent = audioRole === 'music' ? 1 : (model.referenceMediaLimits ?? DEFAULT_REFERENCE_LIMITS).maxAudioClips;
    return {
        images: imageTags,
        videos: Array.from({ length: videoCount }, (_, i) => (videoTakesClips(model, mode) ? videoTag(i) : null)),
        audio: Array.from({ length: audioCount }, (_, i) => (videoTakesAudio(model, mode) && i < audioSent ? audioTag(i) : null)),
        klingSlots: kling,
    };
};
